import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package, Lock, Clock, Target, CheckCircle2, Circle, ArrowLeft, BookOpen, GraduationCap, Wrench, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PublicShell, Loading, EmptyState, LockBadge } from "@/components/shared";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function GrowthPacks() {
  const { data, isLoading } = useQuery({ queryKey: ["packs"], queryFn: async () => (await api.get("/packs")).data });
  return (
    <PublicShell>
      <section className="bg-navy grain"><div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald">CEO Growth Packs</p>
        <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">Curated paths to one clear result</h1>
        <p className="mt-3 max-w-2xl text-slate-400">Each pack bundles the exact playbooks, courses and tools — in order — to achieve a specific outcome.</p>
      </div></section>
      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        {isLoading ? <Loading /> : (data?.length ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((p) => {
              const pct = p.step_count ? Math.round((p.done / p.step_count) * 100) : 0;
              return (
                <Link key={p.id} to={`/packs/${p.slug}`} data-testid={`pack-card-${p.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]">
                  <div className="relative aspect-video overflow-hidden bg-navy">
                    {p.cover_image && <img src={p.cover_image} alt="" className="h-full w-full object-cover opacity-80" />}
                    <div className="absolute left-3 top-3 flex gap-2"><span className="inline-flex items-center gap-1 rounded bg-white/90 px-2 py-1 text-[11px] font-bold text-navy"><Package className="h-3 w-3" /> Pack</span>{p.locked && <LockBadge tier={p.membership_requirement} />}</div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-lg font-semibold text-navy group-hover:text-emerald">{p.title}</h3>
                    <p className="mt-1 flex-1 text-sm text-slate-600">{p.overview}</p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-slate-400"><span className="flex items-center gap-1"><Target className="h-3.5 w-3.5" /> {p.step_count} steps</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {p.required_time}</span></div>
                    {p.done > 0 && <div className="mt-3"><Progress value={pct} className="h-1.5" /></div>}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : <EmptyState title="No Growth Packs yet" />)}
      </section>
    </PublicShell>
  );
}

const STEP_ICON = { resource: BookOpen, course: GraduationCap, tool: Wrench, action: Zap };

export function GrowthPackDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["pack", slug], queryFn: async () => (await api.get(`/packs/${slug}`)).data });
  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  if (!data) return <PublicShell><div className="py-24 text-center text-slate-500">Pack not found.</div></PublicShell>;

  const toggle = async (idx) => {
    if (!user) return navigate("/login");
    try { await api.post(`/packs/${data.id}/steps/${idx}/toggle`); refetch(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upgrade required"); }
  };
  const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;

  return (
    <PublicShell>
      <section className="bg-navy grain"><div className="relative mx-auto max-w-5xl px-5 py-12 lg:px-8">
        <button onClick={() => navigate("/packs")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white" data-testid="pack-back"><ArrowLeft className="h-4 w-4" /> All packs</button>
        <h1 className="font-heading text-3xl font-bold text-white sm:text-4xl">{data.title}</h1>
        <p className="mt-2 max-w-2xl text-slate-400">{data.overview}</p>
        <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-300">
          <span className="flex items-center gap-1.5"><Target className="h-4 w-4 text-emerald" /> {data.expected_outcome}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-emerald" /> {data.required_time}</span>
        </div>
        {!data.locked && <div className="mt-5 max-w-md"><Progress value={pct} className="h-2" /><p className="mt-2 text-sm text-slate-400">{data.done}/{data.total} steps complete</p></div>}
      </div></section>
      <section className="mx-auto max-w-3xl px-5 py-10 lg:px-8">
        {data.locked ? (
          <div className="rounded-xl border border-emerald/30 bg-emerald/5 p-8 text-center" data-testid="pack-locked">
            <Lock className="mx-auto h-8 w-8 text-emerald" />
            <h3 className="mt-4 font-heading text-lg font-semibold text-navy">This Growth Pack is for {data.membership_requirement === "pro" ? "Pro" : "Premium"} members</h3>
            <Button onClick={() => navigate("/pricing")} className="mt-4 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="pack-upgrade-btn">See plans</Button>
          </div>
        ) : (
          <ol className="space-y-3">
            {data.steps.map((s) => {
              const Icon = STEP_ICON[s.type] || Zap;
              return (
                <li key={s.index} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`pack-step-${s.index}`}>
                  <button onClick={() => toggle(s.index)} className="mt-0.5" data-testid={`pack-step-toggle-${s.index}`}>{s.completed ? <CheckCircle2 className="h-6 w-6 text-emerald" /> : <Circle className="h-6 w-6 text-slate-300" />}</button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-slate-400" /><span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.type}</span></div>
                    <p className={`mt-1 font-semibold ${s.completed ? "text-slate-400 line-through" : "text-navy"}`}>{s.title}</p>
                    {s.type === "resource" && s.ref && <Link to={`/resources/${s.ref}`} className="mt-1 inline-block text-sm font-semibold text-emerald hover:underline">Open resource →</Link>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </PublicShell>
  );
}
